import { useCallback, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (input: { client_id: string; callback: (response: { credential: string }) => void; ux_mode: 'popup' }) => void;
          renderButton: (element: HTMLElement, options: Record<string, unknown>) => void;
          cancel: () => void;
        };
      };
    };
  }
}

const SCRIPT_ID = 'google-identity-services';
const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  ?? '703465038126-n2d858ckgg1fe6s3d3bev6kvg5js064u.apps.googleusercontent.com';

export function GoogleButton({ onCredential, text = 'signin_with', disabled = false }: {
  onCredential: (credential: string) => Promise<void>;
  text?: 'signin_with' | 'continue_with';
  disabled?: boolean;
}) {
  const target = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(() => Boolean(window.google));

  useEffect(() => {
    if (window.google) return;
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');
    const loaded = () => setReady(true);
    script.addEventListener('load', loaded);
    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      document.head.appendChild(script);
    }
    return () => script.removeEventListener('load', loaded);
  }, []);

  const initialize = useCallback(() => {
    if (!ready || disabled || !target.current || !window.google || !clientId) return;
    target.current.innerHTML = '';
    window.google.accounts.id.initialize({
      client_id: clientId,
      ux_mode: 'popup',
      callback: ({ credential }) => void onCredential(credential),
    });
    window.google.accounts.id.renderButton(target.current, {
      type: 'standard', theme: 'outline', size: 'large', shape: 'pill', text,
      width: Math.min(360, target.current.clientWidth || 360), logo_alignment: 'left',
    });
  }, [disabled, onCredential, ready, text]);

  useEffect(initialize, [initialize]);
  return <div className="google-button" ref={target}>{disabled ? 'Verifying your account…' : ready ? 'Preparing Google sign-in…' : 'Loading Google sign-in…'}</div>;
}
